export const endpoints = {
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    profile: '/auth/profile',
    uploadProfilePicture: '/auth/upload-profile-picture',
    refreshToken: '/auth/refresh-token',
    changePassword: '/auth/change-password',
  },
  search: {
    general: '/search',
    manuscripts: '/search/manuscripts',
    journals: '/search/journals',
  },
  categories: {
    base: '/categories',
    getAll: '/categories',
    getById: (id) => `/categories/${id}`,
  },
  locations: {
    countries: {
      getAll: '/locations/countries',
      getById: (id) => `/locations/countries/${id}`,
      create: '/locations/countries',
      update: (id) => `/locations/countries/${id}`,
      delete: (id) => `/locations/countries/${id}`,
    },
    states: {
      getAll: '/locations/states',
      getById: (id) => `/locations/states/${id}`,
      create: '/locations/states',
      update: (id) => `/locations/states/${id}`,
      delete: (id) => `/locations/states/${id}`,
    },
  },
  manuscripts: {
    list: '/manuscripts',
    create: '/manuscripts',
    details: (id) => `/manuscripts/${id}`,
    update: (id) => `/manuscripts/${id}`,
    delete: (id) => `/manuscripts/${id}`,
    draft: '/manuscripts/draft',
    submit: (id) => `/manuscripts/${id}/submit`,
    submitRevision: (id) => `/manuscripts/${id}/submit-revision`,
    authorManuscripts: '/manuscripts/author/manuscripts',
    chiefEditorManuscripts: '/manuscripts/chief-editor/manuscripts',
  },
  reviews: {
    list: '/reviews',
    create: '/reviews',
    details: (id) => `/reviews/${id}`,
    update: (id) => `/reviews/${id}`,
    submit: (id) => `/reviews/${id}/submit`,
    complete: (id) => `/reviews/${id}/complete`,
    start: (id) => `/reviews/${id}/start`,
    assign: '/reviews/assign',
    manuscriptReviews: (manuscriptId) => `/reviews/manuscript/${manuscriptId}`,
    reviewerReviews: '/reviews/reviewer/reviews',
    decision: (reviewId) => `/reviews/${reviewId}/decision`,
    removeReviewer: (manuscriptId, reviewerId) => `/reviews/manuscript/${manuscriptId}/reviewer/${reviewerId}`,
    acceptInvitation: (token) => `/reviews/invitation/${token}/accept`,
    declineInvitation: (token) => `/reviews/invitation/${token}/decline`,
    getInvitation: (token) => `/reviews/invitation/${token}`,
    failedInvitations: '/reviews/invitations/failed',
    resendInvitation: (invitationId) => `/reviews/invitation/${invitationId}/resend`,
    updateDueDate: (reviewId) => `/reviews/${reviewId}/due-date`,
    requestRevision: (reviewId) => `/reviews/${reviewId}/revision`,
    inviteNonUser: '/reviews/invite-non-user',
  },
  users: {
    list: '/users',
    details: (id) => `/users/${id}`,
    update: (id) => `/users/${id}`,
    updateRole: (id) => `/users/${id}/role`,
    updateStatus: (id) => `/users/${id}/status`,
    resetPassword: (id) => `/users/${id}/reset-password`,
    getByRole: (role) => `/users/role/${role}`,
    me: '/users/me',
    coauthors: '/users/coauthors',
    editorialBoard: '/users/editorial-board',
  },
  journals: {
    getAll: '/journals',
    getById: (id) => `/journals/${id}`,
    getByCategory: (categoryId) => `/journals/category/${categoryId}`,
    create: '/journals',
    update: (id) => `/journals/${id}`,
    delete: (id) => `/journals/${id}`,
    getByEditor: '/journals/editor/journals',
    approve: (id) => `/journals/${id}/approve`,
    getPendingApprovals: '/journals/pending/approvals',
    addChiefEditor: (id) => `/journals/${id}/chief-editors`,
    removeChiefEditor: (id, chiefEditorId) => `/journals/${id}/chief-editors/${chiefEditorId}`,
    changeMainChiefEditor: (id) => `/journals/${id}/main-chief-editor`,
    uploadCoverPhoto: (id) => `/journals/${id}/cover-photo`,
    deleteCoverPhoto: (id) => `/journals/${id}/cover-photo`,
  },
  volumes: {
    getByJournal: (journalId) => `/volumes/journal/${journalId}`,
    getById: (id) => `/volumes/${id}`,
    create: (journalId) => `/volumes/journal/${journalId}`,
    update: (id) => `/volumes/${id}`,
    delete: (id) => `/volumes/${id}`,
    uploadFile: (volumeId) => `/volumes/${volumeId}/upload-file`,
    downloadFile: (volumeId) => `/volumes/${volumeId}/download-file`,
  },
  issues: {
    getByJournal: (journalId) => `/issues/journal/${journalId}`,
    getByVolume: (volumeId) => `/issues/volume/${volumeId}`,
    getById: (id) => `/issues/${id}`,
    create: (journalId, volumeId) => `/issues/journal/${journalId}/volume/${volumeId}`,
    update: (id) => `/issues/${id}`,
    delete: (id) => `/issues/${id}`,
    uploadFile: (issueId) => `/issues/${issueId}/upload-file`,
    downloadFile: (issueId) => `/issues/${issueId}/download-file`,
  },
  publications: {
    list: '/publications',
    details: (id) => `/publications/${id}`,
    create: '/publications',
    byIssue: (issueId) => `/publications/issue/${issueId}`,
    byJournal: (journalId) => `/publications/journal/${journalId}`,
    byVolume: (volumeId) => `/publications/volume/${volumeId}`,
    byAuthor: (authorId) => `/publications/author/${authorId}`,
    userPublications: '/publications/user/publications',
    updateStatus: (id) => `/publications/${id}/status`,
  },
  attachments: {
    downloadPublicationFile: (id) => `/attachments/publication/${id}/download`,
    downloadAttachment: (id) => `/attachments/${id}/download`,
  },
  statistics: {
    dashboard: '/statistics/dashboard',
    public: '/statistics/public',
    journal: (journalId) => `/statistics/journal/${journalId}`,
  },
  notifications: {
    preferences: {
      get: '/notifications/preferences',
      update: '/notifications/preferences',
    },
  },
  titles: {
    getAll: '/titles',
    getActive: '/titles/active',
    getById: (id) => `/titles/${id}`,
    create: '/titles',
    update: (id) => `/titles/${id}`,
    delete: (id) => `/titles/${id}`,
  },
  dois: {
    // Configuration endpoints
    configurations: {
      create: '/dois/configurations',
      list: '/dois/configurations',
      getById: (id) => `/dois/configurations/${id}`,
      update: (id) => `/dois/configurations/${id}`,
      delete: (id) => `/dois/configurations/${id}`,
      activate: (id) => `/dois/configurations/${id}/activate`,
      getActive: '/dois/configurations/active',
    },
    // DOI management endpoints
    assign: '/dois/assign',
    batchAssign: '/dois/batch-assign',
    unassign: '/dois/unassign',
    regenerate: '/dois/regenerate',
    lookup: '/dois/lookup',
    list: '/dois',
    statistics: '/dois/statistics',
    // Deposit endpoints
    deposit: '/dois/deposit',
    batchDeposit: '/dois/batch-deposit',
    depositJobs: '/dois/deposit-jobs',
    retryFailed: '/dois/retry-failed',
    checkStatus: (doiId) => `/dois/${doiId}/check-status`,
    // Export endpoints
    exportSingle: '/dois/export/single',
    exportBatch: '/dois/export/batch',
    // Helper endpoints
    patternPreviews: '/dois/pattern-previews',
    supportedAgencies: '/dois/supported-agencies',
  },
};
